"use client"

import { useEffect } from "react"
import { Form, Formik, useFormikContext } from "formik"
import { useTranslations } from "next-intl"
import { FormInput } from "@/components/form-inputs"
import { RoleMultiSelect } from "@/components/agents/shared/role-multi-select"
import { Button } from "@/components/ui/button"
import { agentPromptUseCase, type CreationAgentType } from "@/lib/agents/creation/types"
import { WizardStepActions } from "./wizard-step-actions"

export type RoleNameValues = { name: string; roles: string[]; roleLabel: string }

export function RoleNameStep({ type, values, onNext, onChange }: {
  type: CreationAgentType
  values: RoleNameValues
  onChange: (values: RoleNameValues) => void
  onNext: (values: RoleNameValues) => Promise<void>
}) {
  const t = useTranslations("AgentCreation")
  return (
    <Formik
      initialValues={values}
      validate={values => {
        const errors: { name?: string; roles?: string } = {}
        if (!values.name.trim()) errors.name = t("nameRequired")
        if (!values.roles.length) errors.roles = t("roleRequired")
        return errors
      }}
      onSubmit={async (values, helpers) => {
        helpers.setStatus(undefined)
        try {
          await onNext(values)
        } catch (error) {
          helpers.setStatus(error instanceof Error ? error.message : t("failed"))
        }
      }}
    >
      {({ values, errors, touched, setFieldValue, isSubmitting, status }) => (
        <Form className="space-y-6">
          <DraftObserver onChange={onChange} />
          <FormInput name="name" label={t("name")} maxLength={100} />
          <div className="space-y-2">
            <RoleMultiSelect
              presentation="wizard"
              useCase={agentPromptUseCase(type)}
              value={values.roles}
              roleLabel={values.roleLabel}
              onRoleLabelChange={label => void setFieldValue("roleLabel", label)}
              onChange={roles => void setFieldValue("roles", roles)}
              disabled={isSubmitting}
              invalid={Boolean(touched.roles && errors.roles)}
            />
            {touched.roles && errors.roles ? <p role="alert" className="text-sm text-destructive">{errors.roles}</p> : null}
          </div>
          {typeof status === "string" ? <p role="alert" className="text-sm text-destructive">{status}</p> : null}
          <WizardStepActions>
            <Button type="submit" disabled={isSubmitting}>
              <span>{isSubmitting ? t("preparing") : t("next")}</span>
            </Button>
          </WizardStepActions>
        </Form>
      )}
    </Formik>
  )
}

function DraftObserver({ onChange }: { onChange: (values: RoleNameValues) => void }) {
  const { values } = useFormikContext<RoleNameValues>()
  useEffect(() => onChange(values), [onChange, values])
  return null
}
